import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import { BulkNodesEditFormValues } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodesEditForm"
import { getTagErrors } from "@features/builder/NodeDrawer/NodeForm/helpers"
import { Delete } from "@mui/icons-material"
import {
  Autocomplete,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material"
import { Controller, useFormContext, useWatch } from "react-hook-form"

type BulkGraphTagsFieldRowProps = {
  nodeId: string
  index: number
  onDelete: () => void
  graphTagsMap: Map<string, string[]>
  validationErrors: ValidationState["errors"]
}

export const BulkGraphTagsFieldRow = (props: BulkGraphTagsFieldRowProps) => {
  const form = useFormContext<BulkNodesEditFormValues>()
  const rowPath = `nodes.${props.nodeId}.graphTags` as const
  const graphTagsValue = useWatch({ control: form.control, name: rowPath }) ?? []

  const currentlySelectedTag = graphTagsValue[props.index]?.tag ?? ""
  const tagsOptions = Array.from(props.graphTagsMap.keys())
  const valuesOptions = props.graphTagsMap.get(currentlySelectedTag) ?? []
  const rowValidationErrors = getTagErrors(props.validationErrors, currentlySelectedTag)

  return (
    <Grid container item spacing={1}>
      <Grid item xs>
        <Controller
          control={form.control}
          name={`${rowPath}.${props.index}.tag`}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              onChange={(_, value) => {
                field.onChange(value)
                form.setValue(`${rowPath}.${props.index}.values`, [], { shouldDirty: true })
              }}
              disableClearable
              autoHighlight
              autoSelect
              size="small"
              // disable already used tags to avoid defining the same tag multiple times
              getOptionDisabled={(option) => graphTagsValue.some(({ tag }) => tag === option)}
              options={tagsOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={field.name}
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                  label="Tag"
                  InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs>
        <Controller
          control={form.control}
          name={`${rowPath}.${props.index}.values`}
          render={({ field, fieldState }) => (
            <Autocomplete
              multiple
              options={valuesOptions}
              value={field.value ?? []}
              onChange={(_, value) => field.onChange(value)}
              disableClearable={(field.value ?? []).length === 0}
              disabled={field.disabled || !currentlySelectedTag}
              size="small"
              renderTags={(tagsValue, getTagProps) =>
                tagsValue.map((option, tagIndex) => {
                  const { key, ...tagProps } = getTagProps({ index: tagIndex })
                  return <Chip key={key} label={option} size="small" {...tagProps} />
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={field.name}
                  error={!!fieldState.error?.message || rowValidationErrors.length > 0}
                  helperText={fieldState.error?.message}
                  label="Values"
                  InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
                />
              )}
            />
          )}
        />
        {rowValidationErrors.map((error, index) => (
          <FormHelperText key={index} error>
            - {error.msg}
          </FormHelperText>
        ))}
      </Grid>
      <Grid item xs={1}>
        <Tooltip title="Delete">
          <span>
            <IconButton
              color="error"
              size="small"
              disabled={form.formState.disabled}
              onClick={props.onDelete}
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  )
}
